import { useRef, useState, type JSX } from "react";

import { ApiRequestError } from "../../../common/api/apiClient.js";
import type { FileNodeDTO } from "../../../common/api/types.js";
import { PrimaryButton } from "../../../common/components/PrimaryButton.js";
import { ShareDialog } from "../../../common/components/ShareDialog.js";
import { FileList } from "../components/FileList.js";
import { useFilesLocation } from "../filesLocation.js";
import {
  useCreateFolder,
  useDownloadFile,
  useFileShares,
  useFolder,
  useRevokeFileShare,
  useShareFile,
  useSharedWithMe,
  useTrashNode,
  useUploadFile,
} from "../query/fileQueries.js";

/** Dateien: Inhalt des gewählten Bereichs. Die Navigation steht in der Sidebar. */
export function FilesPage(): JSX.Element {
  const location = useFilesLocation();
  const [shareTarget, setShareTarget] = useState<FileNodeDTO | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const folder = useFolder(location.tab === "mine" ? location.folderId : null);
  const shared = useSharedWithMe();
  const createFolder = useCreateFolder(location.folderId);
  const upload = useUploadFile(location.folderId);
  const download = useDownloadFile();
  const trash = useTrashNode(location.folderId);

  const shares = useFileShares(shareTarget?.id ?? null);
  const shareFile = useShareFile(shareTarget?.id ?? null);
  const revokeShare = useRevokeFileShare(shareTarget?.id ?? null);

  const open = (node: FileNodeDTO): void => {
    if (node.kind === "FOLDER") {
      location.openFolder(node.id);
    } else {
      download.mutate(node.id);
    }
  };

  const remove = (node: FileNodeDTO): void => {
    if (window.confirm(`„${node.name}" wirklich löschen?`)) {
      trash.mutate(node.id);
    }
  };

  const addFolder = (): void => {
    const name = window.prompt("Name des Ordners");
    if (name?.trim()) {
      setMessage(null);
      createFolder.mutate(name, { onError: (error) => setMessage(error.message) });
    }
  };

  const isMine = location.tab === "mine";
  const entries = isMine ? (folder.data?.entries ?? []) : (shared.data ?? []);
  const canWriteHere = (folder.data?.folder.level ?? "VIEW") !== "VIEW";
  const activeError = folder.error ?? shared.error ?? upload.error ?? trash.error ?? null;

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <header className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold text-ink">
          {isMine ? (folder.data?.folder.name ?? "Meine Dateien") : "Für mich freigegeben"}
        </h2>
        {isMine ? (
          <div className="ml-auto flex gap-2">
            <PrimaryButton
              type="button"
              onClick={addFolder}
              disabled={!canWriteHere}
              title={canWriteHere ? undefined : "Hier fehlt das Schreibrecht"}
            >
              Ordner
            </PrimaryButton>
            <PrimaryButton
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={!canWriteHere || upload.isPending}
              title={upload.isPending ? "Upload läuft" : undefined}
            >
              {upload.isPending ? "Lädt …" : "Hochladen"}
            </PrimaryButton>
          </div>
        ) : null}
        <input
          ref={fileInput}
          type="file"
          className="hidden"
          onChange={(changed) => {
            const file = changed.target.files?.[0];
            changed.target.value = "";
            if (file) {
              setMessage(null);
              upload.mutate(file, { onError: (error) => setMessage(error.message) });
            }
          }}
        />
      </header>

      {message ? <p className="text-sm text-danger">{message}</p> : null}
      {activeError ? <p className="text-sm text-danger">{activeError.message}</p> : null}

      {folder.isPending && isMine ? (
        <p className="text-sm text-ink-muted">Wird geladen …</p>
      ) : (
        <FileList
          entries={entries}
          onOpen={open}
          onDownload={(node) => download.mutate(node.id)}
          onShare={setShareTarget}
          onTrash={remove}
          showOwner={!isMine}
          selectedId={location.fileId}
        />
      )}

      {shareTarget ? (
        <ShareDialog
          title={shareTarget.name}
          shares={shares.data?.shares ?? []}
          canManage={(shares.data?.node.level ?? shareTarget.level) === "MANAGE"}
          busy={shareFile.isPending || revokeShare.isPending}
          error={
            shareFile.error instanceof ApiRequestError
              ? shareFile.error.message
              : (shares.error?.message ?? null)
          }
          onShare={(input) => shareFile.mutate(input)}
          onRevoke={(shareId) => revokeShare.mutate(shareId)}
          onClose={() => setShareTarget(null)}
        />
      ) : null}
    </div>
  );
}
